/**
 * Feature flags GROUP pilotes entite par entite via le fichier de config PHP
 * `config/feature_flags.php`, expose dans le profil (`dataMyProfile.featureFlags`)
 * et resolu ici selon l'entite courante.
 */
export const FEATURE = {
  /** Widget HelpBot (avatar flottant + popover d'aide). */
  HELPBOT: 'helpbot',
  /** Assistant conversationnel "Neo" (chat texte via ai-core). */
  HELPBOT_CHAT: 'helpbot_chat',
  /** Assistant vocal (agent ElevenLabs) — service en attente, pas encore branche. */
  HELPBOT_VOICE: 'helpbot_voice',
  /** Integration LGO (Pharmony, Leo) cote frontoffice pharmacie Care. */
  LGO: 'lgo',
  /**
   * Facturation via le LGO Leo : retire Leo du choix du LGO quand il est ferme. L'API refuse
   * aussi activation et envoi (LeoProvider), ce flag n'est pas qu'un affichage.
   */
  LGO_LEO: 'lgo_leo',
  /** Mode test (sandbox) Care : banniere + interrupteur frontoffice pharmacie. */
  CARE_SANDBOX: 'care_sandbox',
  /** Extraction IA de conseils depuis la bibliotheque Care (dev/stage uniquement, resolu cote API). */
  CARE_ADVICE_EXTRACTION: 'care_advice_extraction',
  /** Gestion du personnel Care : besoins et planning des collaborateurs. */
  CARE_STAFF_PLANNING: 'care_staff_planning',
  /**
   * Care ouvert aux professionnels de sante hors officine (kine, bilan de prevention seul).
   * Ferme partout en attendant le COMEX : l'API n'accorde alors aucun role Care a un kine et
   * refuse l'activation ; le front masque l'onglet admin Care d'une fiche kine.
   */
  CARE_HEALTH_PROFESSIONAL: 'care_health_professional',
  /**
   * Campagnes SMS/email patients (frontoffice pharmacie Care) : entree de menu et pages
   * /frontoffice/care/campaigns. Resolu par entite ET par environnement cote API
   * (dev/stage uniquement, OFF en prod) — cf. isCareCampaignsEnabled.
   */
  CARE_CAMPAIGNS: 'care_campaigns',
  /**
   * Teleservice INSi (verification d'identite du dossier Care) : chip d'etat d'identite et
   * tout le parcours derriere, recherche par carte Vitale comme par traits.
   */
  AMELI_INSI: 'ameli_insi',
  /** Dossier Medical Partage (bloc DMP du dossier Care). */
  AMELI_DMP: 'ameli_dmp',
  /**
   * CTI Ringover : softphone integre (iframe app.ringover.com) et bouton « Appeler » a la
   * place des liens `tel:` du CRM. Resolu sur l'entite PHARMEDIGROUP ; l'acces reste
   * borne par le role admin back-office et le pays France (useRingoverEligibility).
   */
  RINGOVER_CTI: 'ringover_cti',
  /**
   * Import / mise a jour des pharmacies par fichier (menu Imports de la liste des pharmacies).
   * Coupe le 14/10/2026 : l'entree reste visible, grisee, avec son explication ; l'API refuse
   * aussi l'appel direct (422). Resolu sur l'entite PHARMEDIGROUP.
   */
  PHARMACY_FILE_IMPORT: 'pharmacy_file_import',
  /**
   * Collecte des evenements du parcours School (fiche ouverte, video regardee, recherche).
   * Flag par environnement cote API, OFF en prod tant que le DPO n'a pas valide : coupe, le
   * front n'envoie rien. Resolu sur l'entite PHARMEDIGROUP.
   */
  SCHOOL_EVENTS: 'school_events',
  /**
   * Calendrier de l'Avent School. Flag par environnement cote API, eteint en prod (decision du
   * 01/11/2026). Resolu sur l'entite PHARMEDIGROUP.
   */
  SCHOOL_ADVENT_CALENDAR: 'school_advent_calendar',
  /**
   * Mot de passe renforce (12 caracteres, 3 familles) etendu a tous les comptes de l'extranet,
   * pharmaciens et equipes compris (D-75). Flag global cote API, livre eteint : l'API refuse
   * seule un mot de passe non conforme, le front s'en sert pour valider avant l'envoi.
   */
  PASSWORD_POLICY_EXTRANET: 'password_policy_extranet',
  /**
   * Régie SoundCast en lecture seule (pharmedigroup-api/docs/SOUND_REGIES.md) : choix de la
   * source à la création d'un programme, programmes « SoundCast » rattachés à une campagne
   * existante, plus aucune création chez SoundCast. Par environnement côté API, ouvert en prod
   * depuis le 04/11/2026. Résolu sur l'entité PHARMEDISOUND.
   */
  SOUND_REGIE_SOUNDCAST: 'sound_regie_soundcast',
  /** Régie Dploy (même doc) : programmes « Dploy ». Éteint en prod. Entité PHARMEDISOUND. */
  SOUND_REGIE_DPLOY: 'sound_regie_dploy',
  // Le flag `campaign_participation` a ete SUPPRIME (participation v2) : le workflow vit sur
  // tout programme de campagne actif (la date limite de REFUS qui l'activait a ete retiree le
  // 10/09/2026), la colonne Participation est toujours visible.
} as const

type FeatureFlags = Record<string, Record<string, boolean>>

/**
 * Indique si une feature GROUP est activee pour une entite donnee.
 * Entite absente / non definie => `false` par defaut.
 */
export const isFeatureEnabled = (
  flags: FeatureFlags | undefined | null,
  feature: string,
  entity: string | undefined | null
): boolean => {
  if (!flags || !entity) return false
  return !!flags[feature]?.[entity]
}

/**
 * Facturation electronique (`einvoicing_netsuite`) : quand il est actif, TOUTES les
 * factures purchase sont validees en electronique (plus de PDF/Yousign a la signature)
 * — le PDF final arrive sous 24-48 h. Flag par environnement cote API, expose au profil
 * via PROFILE_ENV_FLAGS sous l'entite PHARMEDIGROUP (booleen deja resolu).
 */
export const isEinvoicingEnabled = (flags: FeatureFlags | undefined | null): boolean =>
  isFeatureEnabled(flags, 'einvoicing_netsuite', 'PHARMEDIGROUP')


/**
 * Mot de passe renforce pour tous les comptes de l'extranet (`password_policy_extranet`, D-75).
 * Flag global cote API, expose au profil via PROFILE_ENV_FLAGS sous l'entite PHARMEDIGROUP.
 */
export const isPasswordPolicyExtranetEnabled = (flags: FeatureFlags | undefined | null): boolean =>
  isFeatureEnabled(flags, FEATURE.PASSWORD_POLICY_EXTRANET, 'PHARMEDIGROUP')
